import React, { useEffect, useState } from "react";
import { Film, Eye, MessageCircle, Coins, MapPin, Calendar, Sparkles, X } from "lucide-react";
import { Button } from "../ui/button";
import { getRecentWatched } from "../../lib/recentWatched";
import { ClapIcon } from "../../lib/icons";
import MakeOfferModal from "./MakeOfferModal";

// MarketplaceVideoOffers — Producer-only panel surfaced inside the
// Marketplace page. Lists the last 5 videos this device watched in the
// home feed and lets the Producer make anonymous offers on any of them.
//
// Design choice: no global "Videos For Sale" list. Producers don't
// browse a catalog — they target videos they've actually seen and
// formed an opinion on. This matches the anonymous-offer model: the
// market is implicit, not explicit, and creators don't get pestered
// by random strangers from a public listings page.
const MarketplaceVideoOffers = () => {
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(null);     // detail overlay state
  const [offerOpen, setOfferOpen] = useState(null); // MakeOfferModal state

  useEffect(() => {
    setItems(getRecentWatched());
  }, []);

  // Re-pull on storage events so two open tabs stay roughly in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "globaleyes:recentWatched:v1") setItems(getRecentWatched());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <div className="mt-8" data-testid="marketplace-videos-section">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <h2 className="text-white text-lg font-semibold flex items-center gap-2">
            <Film className="h-5 w-5 text-emerald-400" /> Make an Offer on a Video
          </h2>
          <p className="text-gray-500 text-xs mt-0.5">
            Your last 5 watched videos. Offers stay private — the producer never sees who's bidding.
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 text-center" data-testid="marketplace-videos-empty">
          <Eye className="h-8 w-8 text-gray-700 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">
            Watch a few videos from the home feed first.
          </p>
          <p className="text-gray-600 text-xs mt-1">
            Anything you spend more than 2 seconds on lands here for offers.
          </p>
        </div>
      ) : (
        <ol className="space-y-1">
          {items.map((v, idx) => (
            <li key={v.id}>
              <button
                onClick={() => setPicked(v)}
                className="w-full flex items-center gap-3 px-3 py-3 bg-gray-900 hover:bg-gray-800 border border-gray-800 hover:border-emerald-500/40 rounded-lg text-left transition-colors"
                data-testid={`marketplace-video-row-${idx}`}
              >
                <span className="shrink-0 w-6 text-emerald-400 font-bold text-sm tabular-nums">#{idx + 1}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-white text-sm font-medium truncate">{v.title}</span>
                  <span className="block text-gray-500 text-xs truncate">
                    {v.producer_username ? `@${v.producer_username}` : 'anon producer'}
                    {(v.city || v.country) ? ` · ${[v.city, v.country].filter(Boolean).join(', ')}` : ''}
                  </span>
                </span>
                <Sparkles className="h-4 w-4 text-emerald-400 shrink-0" />
              </button>
            </li>
          ))}
        </ol>
      )}

      {picked && (
        <VideoDetailOverlay
          video={picked}
          onClose={() => setPicked(null)}
          onMakeOffer={() => { setOfferOpen(picked); setPicked(null); }}
        />
      )}

      {offerOpen && (
        <MakeOfferModal
          videoId={offerOpen.id}
          videoTitle={offerOpen.title}
          onClose={() => setOfferOpen(null)}
          onSent={() => setOfferOpen(null)}
        />
      )}
    </div>
  );
};

// VideoDetailOverlay — bottom-sheet-style preview surfacing key stats
// the Producer needs to anchor an offer price. Deliberately doesn't
// stream the actual video; if they want to re-watch they're one click
// away from the feed.
const VideoDetailOverlay = ({ video, onClose, onMakeOffer }) => {
  const stat = (icon, label, value) => (
    <div className="flex flex-col items-center gap-1 px-2 py-2 bg-gray-900 border border-gray-800 rounded-md min-w-[72px]">
      <div className="text-emerald-400">{icon}</div>
      <div className="text-white text-sm font-semibold tabular-nums">{value}</div>
      <div className="text-gray-500 text-[10px] uppercase tracking-wide">{label}</div>
    </div>
  );

  const posted = video.created_at
    ? new Date(video.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="marketplace-video-overlay">
      <div
        className="bg-gray-950 border border-gray-800 rounded-xl shadow-2xl p-5 max-w-md w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-white text-lg font-semibold leading-tight flex-1">{video.title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white shrink-0"
            data-testid="marketplace-video-overlay-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="text-gray-400 text-sm space-y-1 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Producer:</span>
            <span className="text-emerald-300">{video.producer_username ? `@${video.producer_username}` : 'Anonymous'}</span>
          </div>
          {(video.city || video.country) && (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-gray-500" />
              <span>{[video.city, video.country].filter(Boolean).join(', ')}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-gray-500" />
            <span>Posted {posted}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-5">
          {stat(<Eye className="h-4 w-4" />, "Views", video.views?.toLocaleString() || 0)}
          {stat(<ClapIcon className="h-4 w-4" />, "Applause", video.applause_count?.toLocaleString() || 0)}
          {stat(<MessageCircle className="h-4 w-4" />, "Comments", video.comment_count?.toLocaleString() || 0)}
        </div>

        <Button
          onClick={onMakeOffer}
          className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-semibold py-3"
          data-testid="marketplace-video-overlay-offer-btn"
        >
          <Coins className="h-4 w-4 mr-2" /> Make an Offer
        </Button>
        <p className="text-gray-500 text-[11px] text-center mt-3">
          Your offer is anonymous. The producer sees only the amount + expiry.
        </p>
      </div>
    </div>
  );
};

export default MarketplaceVideoOffers;
